'use client';

import Link from 'next/link';
import { useMemo, useState } from 'react';
import { signIn, getSession, signOut } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Controller, SubmitHandler } from 'react-hook-form';
import {
  PiArrowLeftBold,
  PiArrowRightBold,
  PiShieldCheckBold,
} from 'react-icons/pi';
import { Checkbox, Password, Button, Input } from 'rizzui';
import { Form } from '@core/ui/form';
import { routes } from '@/config/routes';
import { loginSchema, LoginSchema } from '@core/validators/login.schema';
import toast from 'react-hot-toast';
import { useTranslations } from 'next-intl';
import { canAccessPortalPath } from '@/config/access-control';
import {
  getAuthErrorMessage,
  getTwoFactorChallenge,
  type TwoFactorChallenge,
} from '@/app/shared/auth/two-factor';

const initialValues: LoginSchema = {
  login: '',
  password: '',
  rememberMe: false,
};

const DEMO_LOGIN = 'test';
const DEMO_PASSWORD = 'test123';

type BrowserSession = Awaited<ReturnType<typeof getSession>>;

type NavigatorWithUserAgentData = Navigator & {
  userAgentData?: {
    mobile?: boolean;
    platform?: string;
  };
};

function getBrowserName(userAgent: string) {
  if (/Edg\//.test(userAgent)) return 'Edge';
  if (/OPR\/|Opera/.test(userAgent)) return 'Opera';
  if (/CriOS\/|Chrome\//.test(userAgent)) return 'Chrome';
  if (/FxiOS\/|Firefox\//.test(userAgent)) return 'Firefox';
  if (/Safari\//.test(userAgent)) return 'Safari';
  return 'Browser';
}

function getPlatform(nav: NavigatorWithUserAgentData, userAgent: string) {
  if (/iPad|iPhone|iPod/.test(userAgent)) return 'iOS';
  if (/Android/.test(userAgent)) return 'Android';
  if (/CrOS/.test(userAgent)) return 'Chrome OS';
  if (/Windows/.test(userAgent)) return 'Windows';
  if (/Macintosh|Mac OS X/.test(userAgent)) {
    return nav.maxTouchPoints > 1 ? 'iPadOS' : 'macOS';
  }
  if (/Linux/.test(userAgent)) return 'Linux';

  return nav.userAgentData?.platform || nav.platform || 'Unknown';
}

function getLoginDevice() {
  const nav = navigator as NavigatorWithUserAgentData;
  const userAgent = nav.userAgent;
  const platform = getPlatform(nav, userAgent);
  const browser = getBrowserName(userAgent);
  const isTablet =
    /iPad|Tablet/.test(userAgent) ||
    (/Android/.test(userAgent) && !/Mobile/.test(userAgent)) ||
    (/Macintosh/.test(userAgent) && nav.maxTouchPoints > 1);
  const isMobile =
    nav.userAgentData?.mobile === true ||
    /Mobi|Android|iPhone|iPod/.test(userAgent);
  const deviceType = isTablet ? 'tablet' : isMobile ? 'mobile' : 'desktop';

  return {
    device_name: `${browser} on ${platform}`,
    device_type: deviceType,
    platform,
    browser,
  };
}

function canUseDashboardSession(session: BrowserSession) {
  return (
    (session as any)?.error !== 'RefreshAccessTokenError' &&
    typeof session?.user?.accessToken === 'string' &&
    session.user.accessToken.trim().length > 0 &&
    canAccessPortalPath(session.user, routes.dashboard.overview)
  );
}

async function clearRejectedSession(session: BrowserSession) {
  if (session?.user?.accessToken || (session as any)?.error) {
    await signOut({ redirect: false });
  }
}

export default function SignInForm() {
  const t = useTranslations();
  const [isLoading, setIsLoading] = useState(false);
  const [challenge, setChallenge] = useState<TwoFactorChallenge | null>(null);
  const [code, setCode] = useState('');
  const [twoFactorError, setTwoFactorError] = useState('');
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectPath = useMemo(() => {
    const callbackUrl = searchParams?.get('callbackUrl')?.trim();
    if (
      callbackUrl &&
      callbackUrl.startsWith('/') &&
      !callbackUrl.startsWith('//')
    ) {
      return callbackUrl;
    }

    return routes.dashboard.overview;
  }, [searchParams]);

  const createSession = async (payload: unknown) => {
    const result = await signIn('credentials', {
      authProvider: 'verified-session',
      sessionPayload: JSON.stringify(payload),
      redirect: false,
    });

    const fresh = await getSession();
    if (canUseDashboardSession(fresh)) {
      toast.success(t('auth.signin.toast.success'));
      router.replace(redirectPath);
      router.refresh();
      return true;
    }

    await clearRejectedSession(fresh);

    if (result?.error === 'AccessDenied') {
      toast.error(t('auth.signin.toast.notAdmin'));
    } else {
      toast.error(t('auth.signin.toast.noToken'));
    }
    return false;
  };

  const postJson = async (url: string, body: Record<string, unknown>) => {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const payload: any = await response.json().catch(() => null);

    if (!response.ok || payload?.success === false) {
      throw new Error(
        getAuthErrorMessage(payload, t('auth.signin.toast.failed'))
      );
    }

    return payload;
  };

  const onSubmit: SubmitHandler<LoginSchema> = async (data) => {
    setIsLoading(true);
    try {
      const existing = await getSession();
      if (canUseDashboardSession(existing)) {
        toast.success(t('auth.signin.toast.success'));
        router.replace(redirectPath);
        router.refresh();
        return;
      }

      await clearRejectedSession(existing);

      const payload = await postJson('/api/auth/login', {
        login: data.login,
        password: data.password,
        ...getLoginDevice(),
      });

      const nextChallenge = getTwoFactorChallenge(payload);
      if (nextChallenge) {
        setChallenge(nextChallenge);
        setCode('');
        setTwoFactorError('');
        return;
      }

      await createSession(payload);
    } catch (e) {
      console.error('Login error:', e);
      toast.error(
        e instanceof Error && e.message
          ? e.message
          : t('auth.signin.toast.unexpected')
      );
    } finally {
      setIsLoading(false);
    }
  };

  const verifyTwoFactor = async () => {
    if (!challenge || !/^\d{6}$/.test(code)) {
      setTwoFactorError(t('auth.signin.twoFactor.invalidCode'));
      return;
    }

    setIsLoading(true);
    setTwoFactorError('');
    try {
      const payload = await postJson('/api/auth/2fa/verify', {
        challenge_token: challenge.challengeToken,
        code,
        ...getLoginDevice(),
      });
      await createSession(payload);
    } catch (error) {
      setTwoFactorError(
        error instanceof Error && error.message
          ? error.message
          : t('auth.signin.twoFactor.verifyFailed')
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (challenge) {
    return (
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void verifyTwoFactor();
        }}
        className="space-y-5"
      >
        <div className="rounded-[1.1rem] border border-[#03a1fe]/20 bg-[#edf7ff] p-4 dark:border-[#03a1fe]/30 dark:bg-[#07182f]">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-[#0183d0] shadow-sm dark:bg-[#0b2345] dark:text-[#58b7ff]">
              <PiShieldCheckBold className="h-5 w-5" />
            </span>
            <div>
              <p className="text-sm font-semibold text-[#111214] dark:text-white">
                {t('auth.signin.twoFactor.title')}
              </p>
              <p className="mt-1 text-xs leading-5 text-[#65676c] dark:text-white/60">
                {challenge.email
                  ? t('auth.signin.twoFactor.sentTo', {
                      email: challenge.email,
                    })
                  : t('auth.signin.twoFactor.sent')}
                {challenge.expiresIn
                  ? ` ${t('auth.signin.twoFactor.expires', {
                      minutes: Math.ceil(challenge.expiresIn / 60),
                    })}`
                  : ''}
              </p>
            </div>
          </div>
        </div>

        <Input
          id="two-factor-code"
          label={t('auth.signin.twoFactor.codeLabel')}
          placeholder="123456"
          value={code}
          onChange={(event) => {
            setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
            setTwoFactorError('');
          }}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          size="lg"
          inputClassName="!h-14 !rounded-[1rem] !border-black/10 !bg-[#f7f7f5] px-4 text-center font-mono text-xl tracking-[0.45em] shadow-none focus:!border-[#03a1fe] focus:!ring-0 dark:!border-white/10 dark:!bg-[#07182f] dark:!text-white"
          error={twoFactorError}
          autoFocus
        />

        <Button
          type="submit"
          size="lg"
          isLoading={isLoading}
          disabled={isLoading || code.length !== 6}
          className="!h-14 w-full !rounded-full !bg-[#111214] !text-base !font-semibold !text-white shadow-none hover:!bg-black dark:!bg-[#03a1fe] dark:hover:!bg-[#078bd4]"
        >
          {t('auth.signin.twoFactor.verify')}
          <PiArrowRightBold className="ms-2 h-5 w-5" />
        </Button>
        <Button
          type="button"
          variant="text"
          disabled={isLoading}
          onClick={() => {
            setChallenge(null);
            setCode('');
            setTwoFactorError('');
          }}
          className="mx-auto !flex text-[#65676c] dark:text-white/60"
        >
          <PiArrowLeftBold className="me-1.5 h-4 w-4" />
          {t('auth.signin.twoFactor.back')}
        </Button>
      </form>
    );
  }

  return (
    <>
      <Form<LoginSchema>
        validationSchema={loginSchema}
        onSubmit={onSubmit}
        useFormProps={{ mode: 'onChange', defaultValues: initialValues }}
      >
        {({ register, reset, control, formState: { errors } }) => (
          <div className="space-y-5 lg:space-y-3 [@media(max-height:650px)]:space-y-2">
            {process.env.NODE_ENV === 'development' ? (
              <Button
                type="button"
                variant="text"
                onClick={() => {
                  reset({
                    login: DEMO_LOGIN,
                    password: DEMO_PASSWORD,
                    rememberMe: true,
                  });
                }}
                className="!flex !h-auto w-full items-center justify-between gap-4 rounded-[1.1rem] border border-black/[0.06] bg-[#f2f2f2] px-5 py-4 text-left transition hover:border-[#03a1fe]/35 hover:bg-[#edf7ff] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#03a1fe] dark:border-white/10 dark:bg-[#07182f] dark:hover:border-[#03a1fe]/45 dark:hover:bg-[#0a2344] lg:py-2.5 [@media(max-height:650px)]:py-2"
              >
                <span>
                  <span className="block text-sm font-semibold text-[#111214] dark:text-white">
                    {t('auth.signin.demoFill')}
                  </span>
                  <span className="mt-1 block text-xs text-[#77797e] dark:text-white/55">
                    {t('auth.signin.demoHint')}
                  </span>
                </span>
                <span className="shrink-0 rounded-full bg-white px-3 py-1.5 font-mono text-xs font-semibold text-[#03a1fe] shadow-sm dark:bg-[#0b1c3b] dark:shadow-none">
                  {DEMO_LOGIN}
                </span>
              </Button>
            ) : null}
            <Controller
              control={control}
              name="login"
              render={({ field }) => (
                <Input
                  {...field}
                  id="login"
                  type="text"
                  size="lg"
                  value={field.value ?? ''}
                  label={t('auth.signin.loginLabel')}
                  placeholder={t('auth.signin.loginPlaceholder')}
                  className="[&>label>span]:mb-2 [&>label>span]:text-[13px] [&>label>span]:font-semibold [&>label>span]:text-[#303030] dark:[&>label>span]:text-white/80 dark:[&_input::placeholder]:!text-[#8394aa] dark:[&_input]:!text-white"
                  inputClassName="!h-14 !rounded-[1rem] !border-black/10 !bg-[#f7f7f5] px-4 text-sm shadow-none focus:!border-[#03a1fe] focus:!ring-0 dark:!border-white/10 dark:!bg-[#07182f] dark:!text-white dark:placeholder:!text-white/40 lg:!h-12 [@media(max-height:650px)]:!h-11"
                  error={errors.login?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="password"
              render={({ field }) => (
                <Password
                  {...field}
                  id="password"
                  value={field.value ?? ''}
                  label={t('auth.signin.passwordLabel')}
                  placeholder={t('auth.signin.passwordPlaceholder')}
                  size="lg"
                  className="[&>label>span]:mb-2 [&>label>span]:text-[13px] [&>label>span]:font-semibold [&>label>span]:text-[#303030] dark:[&>label>span]:text-white/80 dark:[&_input::placeholder]:!text-[#8394aa] dark:[&_input]:!text-white"
                  inputClassName="!h-14 !rounded-[1rem] !border-black/10 !bg-[#f7f7f5] px-4 text-sm shadow-none focus:!border-[#03a1fe] focus:!ring-0 dark:!border-white/10 dark:!bg-[#07182f] dark:!text-white dark:placeholder:!text-white/40 lg:!h-12 [@media(max-height:650px)]:!h-11"
                  error={errors.password?.message}
                />
              )}
            />
            <div className="flex items-center justify-between gap-4 pb-2 lg:pb-0">
              <Checkbox
                {...register('rememberMe')}
                label={t('auth.signin.rememberMe')}
                variant="flat"
                className="[&>label>span]:text-sm [&>label>span]:font-medium [&>label>span]:text-[#4f5156] dark:[&>label>span]:text-white/70"
                inputClassName="dark:!border-white/20 dark:!bg-[#07182f] dark:checked:!border-[#03a1fe] dark:checked:!bg-[#03a1fe]"
                iconClassName="dark:!text-white"
              />
              <Link
                href={routes.auth.requestResetPassword}
                className="h-auto p-0 text-sm font-semibold text-[#0183d0] transition-colors hover:text-[#111214] dark:text-[#58b7ff] dark:hover:text-white"
              >
                {t('auth.signin.forgot')}
              </Link>
            </div>
            <Button
              className="!h-14 w-full !rounded-full !bg-[#111214] !text-base !font-semibold !text-white shadow-none hover:!bg-black dark:!bg-[#03a1fe] dark:hover:!bg-[#078bd4] lg:!h-12 [@media(max-height:650px)]:!h-11"
              type="submit"
              size="lg"
              isLoading={isLoading}
              disabled={isLoading}
            >
              <span>{t('auth.signin.submit')}</span>
              <PiArrowRightBold className="ms-2 mt-0.5 h-5 w-5" />
            </Button>
          </div>
        )}
      </Form>
    </>
  );
}
